/** @module csi/def */
define([
	"dojo/_base/array",
	"dojo/_base/lang",
	"dojo/promise/all",
	"dojo/topic",
	"dojo/store/Memory",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/modules",
	"csi/_base/underscore",
	"csi/access/_base",
	"csi/access/crews",
	"csi/config/preferences",
	"csi/def/ViewFactory",
	"csi/heading/_base",
	"csi/html",
	"csi/json/JSNode",
	"csi/locale",
	"csi/core/Key",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/session",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/state/viewUtils",
	"csi/util",
	"csi/utility/pathUtility",
	"csi/utility/routerUtility",
	"csi/xml",
	"csi/xml/Query",
], function(array, lang, all, topic, Memory, csi, Deferred, modules, _, access, crews, configPreferences,
	ViewFactory, csiHeading, html, JSNode, locale, Key, reflection, reflectionHints, session, sessionConstants, state, {routerStore, sessionStore}, viewUtils, util,
	{pathCreate}, {LOCATION_TAB, getLocationKey}, xml, Query) {

	const {
		composeViewKey,
		decomposeViewKey,
		getViewAccessRights,
		VIEW_TYPE_GALLERY
	} = viewUtils;

	var DEFAULT_CARD_SUFFIX = ".Default";
	const DEFAULT_MATRIX_CARD_SUFFIX = ".Matrix";
	var HOST_VIEW_ID = "__Host__";
	var RE_HTML_KEY = /(_CS_|_| )/g;
	var SEARCH_CARD = "__Search__";
	const SITE = "Site";

	// Object[]: array of application module XML/JSON documents
	var _modules = [];

	// Promise: the promise to load the merged config
	var _mergedConfigPromise;

	// Object: mapping of type to csi/def/Type
	var _typeMap = {};

	function getHeadingPaths(/*Heading*/defs) {
		// summary: get unique set of paths used by given definitions
		const paths = new Set([""]);
		let path;

		defs.forEach((def) => {
			const {isMatrixGroup, m_path, m_subheadings} = def;

			// special handling is needed for matrix groups so that the full matrix path is used;
			// take the below Heading for example - we don't want to add a path to IssueLog to the view;
			// rather this is a matrix of Issues
			// <Heading Id="Issues" Path="Child:Style/Child:IssueLog" IsMatrixGroup="1">
			path = isMatrixGroup ? def.matrixGroupPath : (def.getClassPath?.() || m_path);
			paths.add(path || "");

			// Headings have nested Headings with paths relative to themselves
			m_subheadings?.forEach((sh) => {
				// sub-headings without a separator are special for rendering container attributes and
				// should not contribute to paths
				if (isMatrixGroup || (sh.m_separator != null)) {
					paths.add(sh.getClassPath());
				}
			});
		});

		return [...paths];
	}

	function _setGlobalHelp(doc) {
		var forms = util.ssn(doc, "/Forms");
		var helpFile = forms && forms.getAttribute("Help");
		if (helpFile) {
			state.set("helpFile", helpFile);
		}
	}

	function _startLoadingMergedConfig() {
		// load merged view xml and css files unless it is development environment
		var fileUrl = !sessionConstants.dev && csi.urlForGenerated("Site_Merged", true);
		if (fileUrl) {
			_mergedConfigPromise = csi.loadScript(fileUrl, "text");
		}
	}

	function _loadMergedConfig() {
		// load merged view xml and css files unless it is development environment
		if (!_mergedConfigPromise) {
			return new Deferred().resolve(true);
		}

		return _mergedConfigPromise.then(
			function(res) {
				_mergedConfigPromise = null;
				res = csi.evalScript(res);
				var doc = JSNode.wrapObject(res);

				_modules.unshift(doc);
				_setGlobalHelp(doc);

				csi.loadCss(csi.urlForGenerated("Site_Merged", true));
				state.set("mergedConfig", true);
				return true;
			},
			function(err) {
				_mergedConfigPromise = null;
				// load individual modules if merged file missing
				return true;
			}
		);
	}

	function _loadModule(/*String*/module, /*String[]*/scripts, /*Boolean*/bOmitAccessControl) {
		var bSite = (module == SITE);
		var fileUrl = bSite ? csi.urlForSite(module) : csi.urlForModule(module + "/" + module);
		return _loadModuleViewXml(scripts, module, fileUrl, bSite, bOmitAccessControl).then(function() {
			// add script for module
			if (!bSite) {
				csi.syncRequire(module);
			}

			// do not load module localization if the merged JSON configuration is loaded already
			if (!bSite && !sessionStore.mergedLocale) {
				scripts.push(module + "/" + locale.ENGLISH);
			}

			return true;
		});
	}

	function _loadModuleViewXml(scripts, module, fileUrl, bSite, bOmitAccessControl) {
		// load view XML. Do not load it if the merged configuration is loaded already.
		if (state.get("mergedConfig")) {
			return new Deferred().resolve(true);
		}

		return csi.loadXml(fileUrl).then(function(xmlDoc) {
			// only load access control if not loading merged files
			if (bSite) {
				_setGlobalHelp(xmlDoc);

				// load Site.css from WebAccess
				csi.loadCss(csi.urlForSite(module));
			} else if (!bOmitAccessControl) {
				scripts.push(module + "/AccessControl");
			}

			_modules.unshift(xmlDoc);
			return true;
		}, function(err) {
			// if loading individual module files, then don't let one unloaded module spoil the bunch.
			return true;
		});
	}

	function _loadSiteJs() {
		// load Site.js from Generated folder, ignore if the file is missing
		const siteModule = state.get("mergedConfig") ? csi.generatedFile("Site_Script", true) : csi.siteFile(SITE);

		function onError(err) {
			console.warn("Error during loading of '" + siteModule + "'. The module is ignored. Error is ", err);

			// show an alert to Administrator
			if (crews.isAdmin()) {
				alert(locale.resource("MISSING_SITE_JS", [siteModule]));
			}
		}

		return csi.loadDojoModule(SITE, siteModule).then(null, onError);
	}

	function _loadDojoModulesDynamically(scripts) {
		// load Site en.js file
		if (!state.get("mergedConfig")) {
			scripts.push(csi.urlForSite(locale.ENGLISH));
		}

		return all(scripts.map(function(moduleId) {
			return csi.loadDojoModule(moduleId);
		}));
	}

	/**
	 * Convert given table view to a gallery view, without actions and default headings
	 * @param {string} nodeType
	 * @param {string} viewId 
	 * @param {Key} imageKey 
	 * @param {cnl} [subtypeUrl] 
	 * @return {GalleryView}
	 */
	function convertTableToGalleryView(nodeType, viewId, imageKey, subtypeUrl) {
		const sourceClass = reflection.getClass(nodeType);
		const tableView = _def.getClassViewDef(sourceClass, viewId);
		const {id, index, path} = imageKey;

		const rel = tableView.getPageRelationship();
		rel.m_news = null;
		rel.m_defActions = null;

		const galleryView = ViewFactory.create(null, VIEW_TYPE_GALLERY, nodeType);

		// keep the id the same as before so that the localization works
		galleryView.m_id = viewId;
		galleryView.m_relationship = rel;

		// for the normal type, do this to avoid sharing the CVs with table view
		let context;

		if (xml.isValidURL(subtypeUrl)) {
			galleryView.m_relationship.addQuery(new Query().RefAttr(reflection.ATTR_SUBTYPE, xml.OP_EQUALS, subtypeUrl));
			context = reflection.getSubtypeName(subtypeUrl);
		}

		galleryView.prefKey = composeViewKey(viewId, nodeType, context, true);
		galleryView.addResource("image", true, Object.assign(galleryView.newHeading(), {
			m_id: id,
			m_index: index,
			m_path: path
		}));
		galleryView.addResource("name", true, Object.assign(galleryView.newHeading(), {
			m_id: xml.ATTR_NAME
		}));

		// add support for printing
		galleryView.classControllerParams = {
			createViewDefinition: () => {
				return _def.convertTableToGalleryView(nodeType, viewId, imageKey);
			}
		};

		return galleryView;
	}

	topic.subscribe(csi.CONFIGURATION_PREFIX_SET_TOPIC, _startLoadingMergedConfig);

	var _def = {
		// String: designates default form view used when creating/editing nodes of a given type
		DEFAULT_FORM_ID: "__Form__",

		// String: default view id for print views
		DEFAULT_PRINT_VIEW_ID: "__Print__",

		// Function: Type constructor filled in by csi/def/Type to avoid circular reference
		//typeCtr: null,

		composeViewKey,
		convertTableToGalleryView,
		decomposeViewKey,

		/**
		 * Load resources for all modules, returning a Promise
		 * @param {boolean} bOmitAccessControl
		 * @return {Promise}
		 */
		init: function(bOmitAccessControl) {
			var timeString = "Performance - loading configuration";
			var scripts = [];

			console.time(timeString);

			// first load merged configuration
			var promise = _loadMergedConfig();

			// then load resources for each module - if merged configuration not loaded, then that will include XML and AccessControl files
			modules.addModule(SITE);

			_.each(modules.getModuleNames(), function(module) {
				promise = promise.then(lang.partial(_loadModule, module, scripts, bOmitAccessControl));
			});

			// if merged configuration not loaded, then load localization files
			promise = promise.then(lang.partial(_loadDojoModulesDynamically, scripts));

			// then load Site.js module as a separate step - ignore if the file doesn't exist
			promise = promise.then(_loadSiteJs);

			return promise.then(() => {
				console.timeEnd(timeString);
			});
		},

		addDebugModule: function() {
			// summary: load Debug module XML and JS
			if (modules.hasModule("Debug")) {
				return;
			}

			return csi.loadXml(csi.urlForModule("Debug/Debug"))
				.then((xmlDoc) => {
					modules.addModule("Debug");
					_modules.unshift(xmlDoc);

					// augment debug type configuration into OOTB
					_.each(util.sn(xmlDoc, "/Forms/NodeRef"), (def) => {
						var defType = this.typeDefinition(def.getAttribute("Type"));
						if (defType) {
							defType.m_defRefs = defType.m_defRefs || [];
							defType.m_defRefs.push(def);
						}
					});
				})
				.then(() => csi.loadDojoModule("Debug/en"))
				.then(() => csi.syncRequire("Debug/main"));
		},

		getModuleDocs: function() {
			return _modules;
		},

		resolveDefs(defs, lmap, rcRoot, nodeRoot) {
			_.each(defs, (obj) => {
				obj.resolve?.(lmap, rcRoot, nodeRoot);
			});
		},

		resolveHeadingInContext: function(/*Context*/ctx, /*Heading*/h) {
			// summary: resolve heading and sub-headings in context of current node;
			// return false if context does not exist for heading paths
			if (h.isResolved) {
				return true;
			}

			if (ctx.getNode() && ctx.push(h.m_path)) {
				var rc = ctx.getNodeClass();
				var lmap = ctx.view.defView.getLocaleMap(rc);

				var bResolved = _.every(h.m_subheadings, function(sh) {
					return this.resolveHeadingInContext(ctx, sh);
				}, this);

				h.resolveHeading(lmap, null, rc);

				ctx.pop();
				return bResolved;
			}

			console.warn("csi/def#resolveHeadingInContext - could not resolve: " + h.m_id);
			return false;
		},

		/**
		 * Return id for default card per type
		 * @param {string} type
		 * @return {string}
		 */
		getDefaultCardId: function(type) {
			return type + DEFAULT_CARD_SUFFIX;
		},

		/**
		 * Return id for default matrix card per type
		 * @param {string} type
		 * @return {string}
		 */
		getDefaultMatrixCardId: function(type) {
			return type + DEFAULT_MATRIX_CARD_SUFFIX;
		},

		getHostViewId: function(/*String?*/type) {
			// summary: return view id for type layout view
			type = type || sessionConstants.userType;
			return type ? ("__" + type + "__") : HOST_VIEW_ID;
		},

		getHtmlViewKey: function(/*String*/type, /*String*/id) {
			// summary: return combination of type and id to be used to form unique key for view;
			// the breadcrumb view may be only case where type is not specified
			var key = (type ? (type + "-") : "") + id;
			return key.replace(RE_HTML_KEY, "");
		},

		getPdfViewKey: function(/*String*/typeMaster, /*String?*/keyContext) {
			// summary: compose URL of preference container for master TDS custom views;
			// if bValid, then check to see if pdf view id is configured and if not return null
			var cc = reflection.getClassConfig(typeMaster);
			var id = cc && cc.getPdfViewId();
			if (!id) return null;

			var typeRev = reflection.getRevisionType(typeMaster);
			return composeViewKey(id, typeRev, keyContext);
		},

		/**
		 * Return Card holding important identifying attributes per type
		 * @param {string} type
		 * @return {Card}
		 */
		getSearchCard: function(type) {
			var typeDef = this.typeDefinition(type);
			return typeDef && typeDef.getCard(SEARCH_CARD);
		},

		getSearchTagsByType: function() {
			// summary: traverse modules to find all Node(Ref)/Search/Type tags mapped by type; make sure to
			// traverse in order such that NodeRefs will override Nodes
			var typeMap = {};
			var add = function(nodeTag) {
				var search = util.ssn(nodeTag, "Search");
				if (search) {
					var typeNode = util.getProperty(nodeTag, "Type");
					var typeTag = util.ssn(search, "Type");
					if (typeTag) {
						typeMap[typeNode] = typeTag;
					} else {
						delete typeMap[typeNode];
					}
				}
			};

			array.forEach(_modules.slice(0).reverse(), function(xn) {
				array.forEach(util.sn(xn, "/Forms/Node"), add);
				array.forEach(util.sn(xn, "/Forms/NodeRef"), add);
			});

			return typeMap;
		},

		getPortalTypeStore: function(/*String*/portalViewId, /*Function?*/filter) {
			// summary: create data stores for types of node that have views in the host and supplier portals
			var items = [];
			const specialTypes = new Set([reflection.TYPE_DATA_SETUP, reflection.TYPE_QUEUES_SERVICES]);

			_.each(reflection.getClasses(), function(rc) {
				var type = rc.getType();

				if (!filter || filter(rc)) {
					if (this.getClassViewDef(rc, portalViewId, false, true) || specialTypes.has(type)) {
						items.push({id: type, name: locale.type(type)});
					}
				}
			}, this);

			return new Memory({
				data: csi.sort(items, "name")
			});
		},

		typeDefinition: function(type) {
			if (!type) return null;

			// if the type was previously found not to exist, then don't look again
			var defType = _typeMap[type];
			if (defType === false) {
				return null;
			}

			// iterate over modules until Node tag is found; also collect NodeRef tags along the way to supplement the Node definition
			if (!defType) {
				var defNode = null;
				var aNodeRef = [];

				array.some(_modules, function(xn) {
					defNode = util.ssn(xn, '/Forms/Node[@Type="' + type + '"]');
					if (defNode) return true;

					var ref = util.ssn(xn, '/Forms/NodeRef[@Type="' + type + '"]');
					if (ref) {
						aNodeRef.push(new this.typeCtr(ref));
					}

					return false;
				}, this);

				if (defType = _typeMap[type] = new this.typeCtr(defNode, aNodeRef, type)) {
					var rcType = reflection.getClass(type);

					// if type has no layout, then check base and/or original classes and store with this type
					if (!defType.layoutView) {
						var defTypeLayout = this.getClassTypeDef(rcType, function(defType) {return !!defType.layoutView;});
						if (defTypeLayout) {
							defType.layoutView = defTypeLayout.layoutView;
						}
					}

					// for master-revision classes, the views belong to the revision, but the navigated type is the master;
					// so when checking if the views are hidden for the site/role, switch the context type to the master
					// FIX: C8-7778 Adding check for getMasterClass when revision class is added in one model without master class (i.e. Workflow)
					if (rcType && rcType.isRevision() && rcType.getMasterClass()) {
						type = rcType.getMasterClass().getType();
					}

					if (defType.layoutView && !configPreferences.isViewHidden(defType.layoutView.getKey(type))) {
						// allow navigation to nodes of type if there is a layout view that has not been hidden for site or role preferences
						defType.bNavigate = true;
					} else if (!defType.layoutView && rcType && rcType.isMaster()) {
						// allow navigation to nodes of master type if can navigate to revision
						var defTypeRevision = this.typeDefinition(rcType.getRevisionClass().getType());
						defType.bNavigate = defTypeRevision && defTypeRevision.bNavigate;
					}

					defType.resolve();
				}
			}

			return defType;
		},

		getTypeDef: function(rcType) {
			return this.typeDefinition(rcType.getType());
		},

		_matchTypeDef: function(type, matchCallback) {
			var typeDef = this.typeDefinition(type);
			return (!typeDef || !matchCallback || matchCallback(typeDef)) ? typeDef : null;
		},

		getNodeTypeDef: function(node, matchCallback) {
			// summary: lookup view on any base classes if not in node's type definition
			if (!node) return null;
			var type = node.$Type;
			return this._matchTypeDef(type, matchCallback) || this.getClassTypeDef(reflection.getClass(type), matchCallback, true);
		},

		getClassTypeDef: function(rcType, matchCallback, bSkipType) {
			// summary: find type definition given root class for any base class for which the callback returns true
			if (!rcType) return null;

			var typeDef;

			rcType.traverse(true, false, function(rc) {
				if (!bSkipType || (rc != rcType)) {
					if (typeDef = this._matchTypeDef(rc.getType(), matchCallback)) {
						return true;
					}
				}
			}, this);

			return typeDef;
		},

		/**
		 * Return Heading defined at class level for given attribute
		 * @param {ReflectionClass} rcType 
		 * @param {C8AttributeId} idAttr 
		 * @param {string} [path]
		 * @return {[element: Element, type: C8NodeType] | undefined}
		 */
		getClassHeading(rcType, idAttr, path) {
			const key = this.getHeadingKey(path, idAttr);
			let element;
			let type;

			this.getClassTypeDef(rcType, function(typeDef) {
				if (element = typeDef.getHeadingXML(key)) {
					type = typeDef.getType();
					return true;
				}

				return false;
			});

			if (element) {
				return [element, type];
			}
		},

		getClassPreferencePaths: function(rcType) {
			return this.getClassTypeDef(rcType)?.getPreferencePaths();
		},

		getClassViewDef: function(/*Class*/rcType, /*String*/idView, /*Boolean?*/bTestAccess, /*Boolean?*/bCheckOnly) {
			// summary: return view for given class and id if it exists, return null otherwise;
			//     if bTestAccess is true and user has no access to view, then return false;
			//     if bCheckOnly is true, then return type for which view is defined if it exists and user has access
			var defType = this.getClassTypeDef(rcType, (t) => t.hasViewDef(idView));
			var defView = this._getTypeViewDef(defType, idView, null, bTestAccess, bCheckOnly, null);
			if (defView && !bCheckOnly) {
				defView.m_rcRoot = rcType;
			}

			return defView;
		},

		getNodeViewDef: function(/*Node*/node, /*String*/idView, /*Boolean?*/bTestAccess, /*Boolean?*/bCheckOnly, /*String?*/typeView, /*String?*/typeLocation) {
			// summary: return view for given node and id if it exists, return null otherwise;
			//	if bTestAccess is true and user has no access to view, then return false;
			//	if bCheckOnly is true, then return type for which view is defined if it exists and user has access
			var defType = this.getNodeTypeDef(node, (t) => t.hasViewDef(idView));
			var defView = this._getTypeViewDef(defType, idView, node, bTestAccess, bCheckOnly, typeView, typeLocation);
			if (defView && !bCheckOnly) {
				defView.m_rcRoot = reflection.getNodeClass(node);
			}

			return defView;
		},

		_getTypeViewDef: function(/*Type*/defType, /*String*/idView, /*Node?*/node, /*Boolean*/bTestAccess, /*Boolean*/bCheckOnly, /*String?*/typeView, /*String?*/typeLocation) {
			// summary: given type containing view, return view definition after
			if (!defType) return null;

			var type = defType.getType();
			var accessRight;

			if (bTestAccess) {
				// NOTE: this api does not support providing viewId with subtypeUrl
				accessRight = getViewAccessRights(typeLocation || routerStore.locationType, type, idView, null, node);

				// return false if no access
				if (!accessRight) {
					return false;
				}
			}

			// if only checking for existence, then return type id if found
			if (bCheckOnly) {
				return type;
			}

			// finally, load view definition
			var defView = defType.getViewDef(idView, typeView);
			if (defView && (accessRight == access.RESOURCE_READ_ONLY_ACCESS)) {
				defView.setReadOnly(true);
			}

			return defView;
		},

		getNodeBrowseURL: function(/*Node*/node, /*String?*/idTargetView, /*Object?*/hashParams) {
			// summary: given node and target view, return href for anchor
			// do not allow browsing to node if there are no views for it, or views have been restricted for role/site
			if (!node || !reflectionHints.canLinkToNode(node) || !this.canNavigateToType(node.$Type)) {
				return null;
			}

			var params = {};

			if (idTargetView) {
				array.forEach(idTargetView.split("|"), function(idView, i) {
					params[getLocationKey(LOCATION_TAB, i)] = idView;
				});
			}

			// add hashParams on top of standard ones (if hashParams are available)
			params = Object.assign(params, hashParams);

			// if creating link on breadcrumb, then use current location tokens as guide to next location;
			// otherwise use just passed tokens so will go to first (or target) tab
			return session.getLocationForUrl(node.$URL, params);
		},

		// Action class
		ACTION_BUTTON: "Button",
		ACTION_ICON: "Icon",
		ACTION_ICON_BUTTON: "ActionIconButton",
		ACTION_LINK: "Link",
		ACTION_TOGGLE_BUTTON: "ToggleButton",


		// Heading class

		/**
		 * @typedef {string} HeadingKey - non-typed key to identify configured (unresolved) Heading
		 */

		/**
		 * Return unique key for given Heading properties
		 * @param {string} [path]
		 * @param {C8AttributeId} [id]
		 * @param {string} [prop]
		 * @param {string} [name]
		 * @param {number | string} [index]
		 * @param {string} [matrix]
		 * @return {HeadingKey}
		 */
		getHeadingKey: function(path, id, prop, name, index, matrix) {
			const key = new Key({
				id,
				index,
				keyRel: null,
				matrix,
				name,
				path,
				prop
			});

			return key.toString();
		},

		/**
		 * Iterate through headings in the view, calling the given function for each heading and sub-heading.
		 * Return true from the function to stop iterating.
		 * @param {Heading[]} headings
		 * @param {(h: Heading, bSubHeading: boolean) => boolean | void} fn
		 * @return {boolean}
		 */
		traverseHeadings(headings, fn) {
			function onHeading(h, bSub) {
				if (!h) {
					return false;
				}

				if (fn(h, bSub)) {
					return true;
				}

				return !!h.m_subheadings?.some((sh) => onHeading(sh, true));
			}

			return !!headings?.some((h) => onHeading(h, false));
		},

		getHeadingPaths,

		getHeadingsDependencyPaths: function(/*Heading[]*/headings, /*String[]?*/paths) {
			// summary: get array of all paths necessary for given headings
			paths = paths || [];

			_.each(headings, function(h) {
				this.getHeadingDependencyPaths(h, paths);
			}, this);

			return paths;
		},

		/**
		 * Get array of all paths necessary for given headings
		 * @param {Heading} h 
		 * @param {string[]} [paths]
		 * @param {boolean} [bApplyMatrixParentScope]
		 * @param {string} [queryPath] 
		 * @param {boolean} [omitSubHeadings] 
		 * @return {string[]}
		 */
		getHeadingDependencyPaths(h, paths, bApplyMatrixParentScope, queryPath, omitSubHeadings) {
			paths ||= [];

			// if Heading is for matrix using Parent path step, then data is queried from view root rather than from view target;
			// so, only process this Heading if not such a matrix Heading or ask explicitly to do so
			var parentScope = h.matrix && h.matrix.parentPathScope;
			if (parentScope && !bApplyMatrixParentScope) {
				return paths;
			}

			// if need to post-process heading paths due to parentScope, then create new array rather than add to existing one
			var results = parentScope ? [] : paths;

			var headingPath = h.getClassPath();
			queryPath ||= h.getQueryPath();

			if (queryPath) {
				results.push(queryPath);
			}

			var ren = csiHeading.getHeadingRenderer(null, h);
			if (ren) {
				ren.addRendererDependencyPaths(h, results);
			}

			_.each(h.depPaths, function(p) {
				results.push(xml.path(headingPath, p));
			}, this);

			_.each(h.m_rules, function(r) {
				this.getRuleDependencyPaths(r, results, headingPath);
			}, this);

			if (h.m_action) {
				this.getActionDependencyPaths(h.m_action, results, headingPath);
			}

			if (h.linkPath) {
				results.push(xml.path(queryPath, h.linkPath));
			}

			if (!omitSubHeadings) {
				this.getHeadingsDependencyPaths(h.m_subheadings, results);
			}

			// replace heading path with parent scope
			if (parentScope) {
				var matrixPaths = _.uniq(results);
				var replacePath = parentScope + "/";
				var re = new RegExp("^" + headingPath + "/");
				var b;

				_.each(matrixPaths, function(p) {
					if (p != headingPath) {
						paths.push(p.replace(re, replacePath));
						b = true;
					}
				});

				if (!b) {
					paths.push(parentScope);
				}
			}

			return paths;
		},

		getActionDependencyPaths: function(/*Action*/a, /*String[]?*/paths, /*String?*/pathContext) {
			// summary: add set of paths hard-coded into relationship (if any)
			paths = paths || [];

			if (a.m_path) {
				pathContext = xml.path(pathContext, a.m_path);
				paths.push(pathContext);
			}

			_.each(a.m_rules, function(r) {
				this.getRuleDependencyPaths(r, paths, pathContext);
			}, this);

			// if ViewRef has a path, get it now as the node at the end of the path is needed to compute data store for drop-down (and drag fill);
			// there are actually not very many cases where there is a path, and that the path would not already be loaded
			// because of other dependencies
			if (a.m_viewRef && a.m_viewRef.m_path) {
				paths.push(xml.path(pathContext, a.m_viewRef.m_path));
			}

			return paths;
		},

		getRelationshipDependencyPaths: function(/*Relationship*/rel, /*String[]?*/paths) {
			// summary: add set of paths hard-coded into relationship (if any)
			paths = paths || [];

			if (rel.m_depPaths) {
				paths = paths.concat(rel.m_depPaths);
			}

			_.each(rel.m_defActions && rel.m_defActions.getActions(), function(a) {
				this.getActionDependencyPaths(a, paths);
			}, this);

			_.each(rel.m_defActions && rel.m_defActions.m_rules, function(r) {
				this.getRuleDependencyPaths(r, paths);
			}, this);

			_.each(rel.m_rules, function(r) {
				this.getRuleDependencyPaths(r, paths);
			}, this);

			return paths;
		},

		getRuleDependencyPaths: function(/*Rule*/rule, /*String[]?*/paths, /*String?*/pathContext) {
			// summary: add set of paths hard-coded into relationship (if any)
			paths = paths || [];

			if (rule.m_path) {
				paths.push(xml.path(pathContext, rule.m_path));
			}

			return paths;
		},

		getCSS: function(/*Object*/obj, /*String?*/classExtra, /*Node?*/node, /*Cache?*/cache, /*Context?*/ctx) {
			// summary: determine CSS for given configuration object, including explicit classes as well as any rules
			var css = [];

			if (classExtra) {
				css.push(classExtra);
			}

			if (obj) {
				if (obj.m_class) {
					css.push(obj.m_class);
				}

				if (obj.m_rules && node) {
					var rule, i = 0;

					while (rule = obj.m_rules[i++]) {
						if (rule.m_class && rule.passes(node, cache, ctx, obj)) {
							css.push(rule.m_class);
						}
					}
				}
			}

			return css.join(" ");
		},

		processClass: function(/*Object*/obj, /*DOMNode*/element, /*String?*/classExtra, /*Node?*/node, /*Cache?*/cache, /*Context?*/ctx) {
			// summary: apply CSS resulting from configuration object to given element
			var cOld = html.getClass(element) || "";
			var cNew = this.getCSS(obj, classExtra, node, cache, ctx);
			if (cNew && (cNew != cOld)) {
				html.setClass(element, cNew);
			}
		},

		processRules: function(/*Context*/ctx, /*Rule[]*/rules) {
			// summary: process rules return true only if all rules passed
			var node = ctx.getContextNode();
			var cache = ctx.cache;

			return !rules || array.every(rules, function(rule) {
				return rule.passes(node, cache, ctx);
			});
		},

		/**
		 * Return CSS classes for rules that pass conditions
		 * @param {Context} ctx
		 * @param {Rule[]} rules
		 * @param {string[]} [classes]
		 * @return {string[]}
		 */
		processRulesCSS: function(ctx, rules, classes) {
			const node = ctx.getNode();
			const cache = ctx.cache;

			classes = classes || [];

			array.forEach(rules, function(rule) {
				if (rule.m_class && rule.passes(node, cache, ctx)) {
					classes.push(rule.m_class);
				}
			});

			return classes;
		},

		createBrowseAnchor: function(/*Context*/ctx,
									 /*DOMNode*/refNode,
									 /*Node*/node,
									 /*String?*/idView,
									 /*String?*/pos,
									 /*Object?*/hashParams) {
			// do nothing if printing, if in dialog (idView=false), or if node is same as current browse node
			if ((idView === false) || !ctx.canLink()) {
				return null;
			}

			if (node) {
				// make sure to not create a link to current location - except special case where it is in PageHeader view
				// and we want to render user link even though may be on that page
				const {locationUrl, locationRevisionUrl} = routerStore;
				const url = node.$URL;
				
				if (xml.nodeIsArchived(node)) {
					return null;
				}

				if (!ctx.bForceBrowse && ((url == locationUrl) || (url == locationRevisionUrl))) {
					return null;
				}

				// find link for anchor - if not valid, then do nothing more
				const href = this.getNodeBrowseURL(node, idView, hashParams);
				if (href) {
					const params = {
						"class": html.CSS_BROWSE,
						href
					};

					const target = ctx.fd?.linkTarget;
					if (target) {
						params.target = target;
					}

					// track url where navigation is coming from if view belongs to tab layout
					// NOTE: this rules out last visited, favorites, global search;
					// the only other place tabs are used are in process management content view,
					// but those do not yet use React tabs / layout.
					// When fully React, use a context provider to specify this
					if (!routerStore.renderDataSetup && ctx.view.layoutItem) {
						params.onclick = () => {
							routerStore.trackDataSetupReturnUrl = true;
						}
					}

					return html.a(params, refNode, pos);
				}
			}

			return null;
		},

		/**
		 * Return true if user can navigate to nodes of the given type
		 * @param {string} type
		 * @return {boolean}
		 */
		canNavigateToType: function(type) {
			var defType = this.typeDefinition(type);
			return defType && defType.bNavigate;
		},

		createBrowseAnchorForType: function(/*Class*/rc, /*String*/url, /*DOMNode*/refNode, /*String?*/pos) {
			// summary: simpler version of above when only url is known
			if (this.canNavigateToType(rc.getType())) {
				return html.create("a", {
					"class": html.CSS_BROWSE,
					href: session.getLocationForUrl(url)
				}, refNode, pos);
			}

			return null;
		}
	};

	return _def;
});
